<template>
  <div class="main-wrapper pos-main-wrapper">
    <header class="dream-pos-header">
      <div class="dream-pos-left">
        <router-link class="dream-logo" :to="{ name: 'index' }">
          <img src="@/assets/img/logo.png" alt="Dreams" />
        </router-link>
        <span class="dream-pos-label">POS</span>
        <span class="dream-clock"><i class="fas fa-clock"></i>{{ currentTime }}</span>
      </div>

      <div class="dream-pos-right">
        <router-link class="dream-dashboard-btn" :to="{ name: 'index' }">
          <i class="fas fa-tachometer-alt"></i>
          <span>Dashboard</span>
        </router-link>
        <router-link class="dream-store-btn" :to="{ name: 'saleslist' }">
          <i class="fas fa-receipt"></i>
          <span>Sales</span>
          <i class="fas fa-chevron-down"></i>
        </router-link>
        <span class="dream-divider"></span>
        <router-link class="dream-icon-btn active" :to="{ name: 'saleslist' }" title="View orders">
          <i class="fas fa-list-alt"></i>
        </router-link>
        <router-link class="dream-icon-btn" :to="{ name: 'salesreport' }" title="Transactions">
          <i class="fas fa-chart-bar"></i>
        </router-link>
        <button type="button" class="dream-icon-btn" title="Reset sale" @click="resetSale({ keepProducts: true })">
          <i class="fas fa-sync-alt"></i>
        </button>
        <img class="dream-avatar" src="@/assets/img/profiles/avatar-01.jpg" alt="" />
      </div>
    </header>

    <div class="page-wrapper pos-page-wrapper">
      <div class="content pos-content">
        <div class="pos-actions-bar">
          <div class="pos-action-group">
            <router-link class="pos-top-btn pos-top-btn-green" :to="{ name: 'saleslist' }">
              <i class="fas fa-shopping-cart"></i>
              View Orders
            </router-link>
            <button type="button" class="pos-top-btn pos-top-btn-violet" @click="resetSale({ keepProducts: true })">
              <i class="fas fa-redo-alt"></i>
              Reset
            </button>
            <router-link class="pos-top-btn pos-top-btn-blue" :to="{ name: 'salesreport' }">
              <i class="fas fa-exchange-alt"></i>
              Transaction
            </router-link>
          </div>
          <span class="pos-action-spacer"></span>
        </div>

        <div class="pos-grid">
          <main class="pos-products-side">
            <section class="pos-section">
              <div class="pos-section-title-row">
                <h4>Categories</h4>
                <span>{{ selectedCategorySummary.count }} Items | {{ formatQty(selectedCategorySummary.available) }} Pcs</span>
              </div>
              <div class="pos-category-scroller" @scroll="handleCategoryScroll" @wheel.prevent="handleCategoryWheel">
                <button
                  v-for="category in categoryCards"
                  :key="category.id"
                  type="button"
                  class="pos-category-card"
                  :class="{ active: selectedCategory === category.id }"
                  @click="selectCategory(category.id)"
                >
                  <img :src="categoryImage(category)" alt="" @error="setDefaultImage" />
                  <strong>{{ category.name }}</strong>
                  <span>{{ category.count }} Items</span>
                  <small>{{ formatQty(category.available) }} Pcs</small>
                </button>
              </div>
            </section>

            <section class="pos-section pos-products-section">
              <div class="pos-section-title-row pos-products-head">
                <div>
                  <h4>Products</h4>
                  <span class="pos-product-count-text">{{ selectedCategorySummary.name }} | {{ selectedCategorySummary.count }} Items | {{ formatQty(selectedCategorySummary.available) }} Pcs</span>
                </div>
                <div class="pos-search">
                  <i class="fas fa-search"></i>
                  <input v-model="productSearch" type="text" placeholder="Search Product" @input="queueProductSearch" />
                </div>
              </div>

              <div v-if="loadingItems" class="pos-empty-panel">Loading products...</div>
              <div v-else-if="!products.length" class="pos-empty-panel">
                No products found for this search.
              </div>
              <div v-else class="pos-product-scroll" @scroll="handleProductScroll">
                <div class="pos-product-grid">
                <button
                  v-for="product in products"
                  :key="product.itemid"
                  type="button"
                  class="pos-product-card"
                  :class="{ selected: isInCart(product.itemid) }"
                  @click="addProduct(product)"
                >
                  <span v-if="isInCart(product.itemid)" class="pos-check"><i class="fas fa-check"></i></span>
                  <span class="pos-product-image">
                    <img :src="productImage(product)" alt="" @error="setDefaultImage" />
                  </span>
                  <span class="pos-product-meta">{{ product.itemcategory || "Product" }}</span>
                  <strong>{{ product.itemname }}</strong>
                  <span class="pos-product-footer">
                    <span>Available {{ formatQty(product.available) }} Pcs</span>
                    <strong>{{ formatCurrency(getSalePrice(product)) }}</strong>
                  </span>
                </button>
                </div>
                <div v-if="loadingMoreItems" class="pos-load-more">Loading more products...</div>
                <div v-else-if="products.length && !hasMoreItems" class="pos-load-more">All products loaded</div>
              </div>
            </section>
          </main>

          <aside class="pos-order-side">
            <div class="pos-order-card">
              <div class="pos-order-head">
                <div>
                  <h3>Order List</h3>
                  <p>Transaction ID : {{ form.billno || "New Sale" }}</p>
                </div>
                <button type="button" title="Clear cart" @click="clearCart">
                  <i class="fas fa-trash"></i>
                </button>
              </div>

              <section class="pos-customer-section">
                <h4>Customer Information</h4>
                <div class="pos-customer-row">
                  <div class="pos-lookup">
                    <input
                      v-model="customerSearch"
                      type="text"
                      placeholder="Walk in Customer"
                      @focus="showCustomerResults = true"
                      @input="searchCustomers"
                    />
                    <button type="button" @click="clearCustomer">
                      <i class="fas fa-chevron-down"></i>
                    </button>
                    <div v-if="showCustomerResults" class="pos-lookup-results">
                      <button
                        v-for="customer in customerSearchResults"
                        :key="customer.customerid"
                        type="button"
                        @mousedown.prevent="selectCustomer(customer)"
                      >
                        <strong>{{ customer.customername || "Unnamed customer" }}</strong>
                        <span>{{ customer.mobile || "No mobile" }}</span>
                      </button>
                      <div v-if="customerSearchLoading" class="pos-lookup-empty">Searching customers...</div>
                      <div v-else-if="customerSearch.trim() && !customerSearchResults.length" class="pos-lookup-empty">
                        No customer found. Use Add Customer.
                      </div>
                    </div>
                  </div>
                  <button type="button" class="pos-add-customer" title="Add customer" @click="openCustomerModal">
                    <i class="fas fa-user-plus"></i>
                  </button>
                </div>
                <div class="pos-product-select">
                  <i class="fas fa-barcode"></i>
                  <input
                    v-model="quickProductSearch"
                    type="text"
                    placeholder="Search Products / Scan Barcode"
                    @keyup.enter="handleQuickProductEntry"
                  />
                  <button type="button" @click="handleQuickProductEntry">
                    <i class="fas fa-chevron-down"></i>
                  </button>
                </div>
              </section>

              <section class="pos-cart-section">
                <div class="pos-cart-title">
                  <h4>Product Added <span>{{ cart.length }}</span></h4>
                  <button type="button" @click="clearCart"><i class="fas fa-times"></i> Clear all</button>
                </div>

                <div v-if="!cart.length" class="pos-empty-cart">Select products to start billing.</div>
                <div v-else class="pos-cart-list">
                  <div v-for="(item, index) in cart" :key="item.itemid" class="pos-cart-item">
                    <img :src="productImage(item)" alt="" @error="setDefaultImage" />
                    <div class="pos-cart-info">
                      <span class="pos-code">{{ item.itemcode || item.barcode || item.itemid }}</span>
                      <strong>{{ item.itemname }}</strong>
                      <span>{{ formatCurrency(item.rate) }}</span>
                    </div>
                    <div class="pos-cart-qty">
                      <button type="button" @click="decreaseQty(item)"><i class="far fa-minus-square"></i></button>
                      <input
                        v-model.number="item.quantity"
                        type="number"
                        min="1"
                        :max="item.layerRemainingQty || item.available || undefined"
                        @input="calculateCartItem(item)"
                      />
                      <button type="button" @click="increaseQty(item)"><i class="far fa-plus-square"></i></button>
                    </div>
                    <button type="button" class="pos-cart-remove" @click="removeCartItem(index)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </section>

              <section class="pos-adjustments">
                <label>
                  <span>Order Tax</span>
                  <select disabled>
                    <option>Auto GST</option>
                  </select>
                </label>
                <label>
                  <span>Round Off</span>
                  <input v-model.number="form.roundoff" type="number" step="0.01" @input="computeTotals" />
                </label>
                <label>
                  <span>Discount</span>
                  <input v-model.number="orderDiscountPercent" type="number" min="0" max="100" @input="computeTotals" />
                </label>
              </section>

              <section class="pos-total-box">
                <div><span>Sub Total</span><strong>{{ formatCurrency(rawSubtotal) }}</strong></div>
                <div><span>Tax</span><strong>{{ formatCurrency(totals.totalTax) }}</strong></div>
                <div v-if="totals.cgst"><span>CGST</span><strong>{{ formatCurrency(totals.cgst) }}</strong></div>
                <div v-if="totals.sgst"><span>SGST</span><strong>{{ formatCurrency(totals.sgst) }}</strong></div>
                <div v-if="totals.igst"><span>IGST</span><strong>{{ formatCurrency(totals.igst) }}</strong></div>
                <div><span>Discount</span><strong class="danger">- {{ formatCurrency(orderDiscountAmount) }}</strong></div>
                <div><span>Round Off</span><strong>{{ formatCurrency(form.roundoff || 0) }}</strong></div>
                <div class="pos-total-line"><span>Total</span><strong>{{ formatCurrency(totals.netAmount) }}</strong></div>
              </section>

              <section class="pos-payment-section">
                <h4>Payment Method</h4>
                <div class="pos-payment-grid">
                  <button
                    v-for="mode in paymentModes"
                    :key="mode.value"
                    type="button"
                    :class="{ active: form.paymentmode === mode.value }"
                    @click="form.paymentmode = mode.value"
                  >
                    <i :class="mode.icon"></i>
                    <span>{{ mode.label }}</span>
                  </button>
                </div>
                <button type="button" class="pos-submit-btn" :disabled="saving || !cart.length" @click="submitPosSale">
                  {{ saving ? "Saving..." : "Create Sale & Print Bill" }}
                </button>
              </section>
            </div>
          </aside>
        </div>
      </div>
    </div>

    <div v-if="showCustomerModal" class="pos-modal-backdrop">
      <div class="pos-customer-modal" role="dialog" aria-modal="true" aria-labelledby="pos-customer-modal-title">
        <div class="pos-modal-head">
          <div>
            <h4 id="pos-customer-modal-title">Add Customer</h4>
            <p>Create and select this customer for the POS sale.</p>
          </div>
          <button type="button" @click="closeCustomerModal"><i class="fas fa-times"></i></button>
        </div>
        <div class="pos-modal-body">
          <label>
            <span>Customer Name</span>
            <input v-model="customerModalForm.customername" type="text" placeholder="Customer name" />
          </label>
          <label>
            <span>Mobile</span>
            <input v-model="customerModalForm.mobile" type="text" placeholder="Phone number" />
          </label>
          <label>
            <span>GST No</span>
            <input v-model="customerModalForm.gstno" type="text" placeholder="Optional GST number" />
          </label>
          <label>
            <span>Address</span>
            <textarea v-model="customerModalForm.address" rows="3" placeholder="Optional address"></textarea>
          </label>
        </div>
        <div class="pos-modal-actions">
          <button type="button" class="pos-modal-cancel" @click="closeCustomerModal">Cancel</button>
          <button type="button" class="pos-modal-save" :disabled="customerModalSaving" @click="createCustomerFromModal">
            {{ customerModalSaving ? "Saving..." : "Save Customer" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { getCompanySettingsCached } from "@/services/companySettings";
import { printBillHtml } from "@/assets/utils/billPrint";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";

const fallbackImages = [
  "product1.jpg",
  "macbook.png",
  "product60.jpg",
  "product44.jpg",
  "product34.jpg",
  "product56.jpg",
  "product67.png",
  "product63.png",
  "product69.jpg",
];

export default {
  name: "pos",
  data() {
    return {
      loadingItems: false,
      saving: false,
      currentTime: "",
      clockTimer: null,
      selectedCategory: "all",
      productSearch: "",
      productSearchTimer: null,
      quickProductSearch: "",
      scanText: "",
      customerSearch: "",
      showCustomerResults: false,
      customerLookupTimer: null,
      categoriesFromApi: [],
      categorySliceStart: 0,
      categoryPageSize: 24,
      categoryLoading: false,
      hasMoreCategories: true,
      products: [],
      productPage: 1,
      productPageSize: 40,
      productTotal: 0,
      hasMoreItems: true,
      loadingMoreItems: false,
      customerSearchResults: [],
      companySettings: {},
      shopStateCode: null,
      orderDiscountPercent: 0,
      form: this.getInitialForm(),
      cart: [],
      totals: this.getInitialTotals(),
      paymentModes: [
        { value: "Cash", label: "Cash", icon: "fas fa-money-bill-wave" },
        { value: "Card", label: "Debit Card", icon: "far fa-credit-card" },
        { value: "UPI", label: "Scan", icon: "fas fa-qrcode" },
      ],
    };
  },
  computed: {
    categoryCards() {
      const cards = [
        {
          id: "all",
          name: "All Categories",
          count: this.productTotal || this.products.length,
          available: this.products.reduce((sum, item) => sum + Number(item.available || 0), 0),
          image: this.assetProductImage("product1.jpg"),
        },
      ];

      const stats = this.products.reduce((acc, item) => {
        const id = item.categoryid || item.itemcategory || "uncategorized";
        if (!acc[id]) acc[id] = { count: 0, available: 0 };
        acc[id].count += 1;
        acc[id].available += Number(item.available || 0);
        return acc;
      }, {});

      this.categoriesFromApi.forEach((category, index) => {
        const id = category.categoryid || category.id || category.categoryname;
        const stat = stats[id] || stats[category.categoryname] || { count: 0, available: 0 };
        cards.push({
          id,
          name: category.categoryname || category.name || "Category",
          count: stat.count,
          available: stat.available,
          image: category.image_url || this.assetProductImage(fallbackImages[(index + 3) % fallbackImages.length]),
        });
      });

      if (cards.length === 1) {
        Object.keys(stats).forEach((id, index) => {
          cards.push({
            id,
            name: id === "uncategorized" ? "Uncategorized" : String(id),
            count: stats[id].count,
            available: stats[id].available,
            image: this.assetProductImage(fallbackImages[(index + 1) % fallbackImages.length]),
          });
        });
      }

      return cards;
    },
    filteredProducts() {
      return this.products;
    },
    selectedCategorySummary() {
      const selected = this.categoryCards.find((category) => String(category.id) === String(this.selectedCategory));
      return selected || {
        name: "Products",
        count: this.productTotal || this.products.length,
        available: this.filteredProducts.reduce((sum, item) => sum + Number(item.available || 0), 0),
      };
    },
    rawSubtotal() {
      return this.cart.reduce((sum, item) => sum + this.lineTotal(item), 0);
    },
    orderDiscountAmount() {
      return Number(((this.rawSubtotal * (Number(this.orderDiscountPercent) || 0)) / 100).toFixed(2));
    },
    isInterState() {
      if (!this.form.gstno || !this.shopStateCode) return false;
      return String(this.form.gstno).substring(0, 2) !== this.shopStateCode;
    },
  },
  async created() {
    await Promise.all([
      this.fetchItems(),
      this.fetchCategories(),
      this.fetchNextBillNo(),
      this.fetchShopStateCode(),
      this.fetchCompanySettings(),
    ]);
  },
  mounted() {
    this.updateClock();
    this.clockTimer = setInterval(this.updateClock, 1000);
    document.addEventListener("mousedown", this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    clearTimeout(this.customerLookupTimer);
    clearTimeout(this.productSearchTimer);
    clearInterval(this.clockTimer);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    clearTimeout(this.customerLookupTimer);
    clearTimeout(this.productSearchTimer);
    clearInterval(this.clockTimer);
  },
  methods: {
    updateClock() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    },
    getToday() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    },
    getInitialForm() {
      return {
        billno: "",
        billdate: this.getToday(),
        customerid: "",
        customername: "",
        mobile: "",
        gstno: "",
        paymentmode: "Cash",
        roundoff: 0,
        remarks: "POS Sale",
      };
    },
    getInitialTotals() {
      return {
        totalAmount: 0,
        taxableValue: 0,
        totalDiscount: 0,
        totalTax: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        netAmount: 0,
      };
    },
    assetProductImage(fileName) {
      try {
        return require(`../../../assets/img/product/${fileName}`);
      } catch {
        return require("../../../assets/img/product/noimage.png");
      }
    },
    defaultProductImage() {
      return this.assetProductImage("default-product.svg");
    },
    productImage(product) {
      if (product?.image_url) return product.image_url;
      if (product?.preview_url) return product.preview_url;
      return this.defaultProductImage();
    },
    categoryImage(category) {
      return category?.image || this.defaultProductImage();
    },
    setDefaultImage(event) {
      event.target.src = this.defaultProductImage();
    },
    formatCurrency(value) {
      return `Rs. ${Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    },
    getSalePrice(item) {
      return Number(item.saleprice ?? item.sellingprice ?? item.unitprice ?? item.mrp ?? 0);
    },
    lineTotal(item) {
      const amount = Number(item.amount || 0);
      const tax = Number(item.taxamount || 0);
      return item.istaxinclusive ? amount : amount + tax;
    },
    getLocationId() {
      const locations = this.$store.getters.getLocations || [];
      return this.$store.getters.getCurrentLocation?.locationid || locations[0]?.locationid || null;
    },
    async fetchItems({ reset = true } = {}) {
      if ((this.loadingItems || this.loadingMoreItems) && !reset) return;
      if (reset) {
        this.loadingItems = true;
        this.productPage = 1;
        this.products = [];
        this.productTotal = 0;
        this.hasMoreItems = true;
      } else {
        if (!this.hasMoreItems) return;
        this.loadingMoreItems = true;
      }
      try {
        const params = {
          stocked_only: true,
          page: this.productPage,
          page_size: this.productPageSize,
        };
        const search = this.productSearch.trim();
        if (search) params.search = search;
        if (this.selectedCategory !== "all") params.category = this.selectedCategory;
        const response = await axios.get("/v1/sales/items", { params });
        const payload = response.data || {};
        const items = Array.isArray(payload) ? payload : payload.items || [];
        this.products = reset ? items : [...this.products, ...items];
        this.productTotal = Number(payload.total ?? this.products.length);
        this.hasMoreItems = this.products.length < this.productTotal;
        if (this.hasMoreItems) this.productPage += 1;
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Products", text: "Failed to load POS products." });
      } finally {
        this.loadingItems = false;
        this.loadingMoreItems = false;
      }
    },
    async fetchCategories({ reset = true } = {}) {
      if (this.categoryLoading && !reset) return;
      if (reset) {
        this.categorySliceStart = 0;
        this.categoriesFromApi = [];
        this.hasMoreCategories = true;
      }
      if (!this.hasMoreCategories) return;
      this.categoryLoading = true;
      try {
        const response = await axios.get("/v1/category/slice", {
          params: {
            start: this.categorySliceStart,
            end: this.categorySliceStart + this.categoryPageSize,
          },
        });
        const categories = response.data || [];
        this.categoriesFromApi = reset ? categories : [...this.categoriesFromApi, ...categories];
        this.categorySliceStart += categories.length;
        this.hasMoreCategories = categories.length >= this.categoryPageSize;
      } catch (error) {
        console.error(error);
      } finally {
        this.categoryLoading = false;
      }
    },
    async fetchNextBillNo() {
      try {
        const response = await axios.get("/v1/sales/next-billno", {
          params: { billdate: this.form.billdate },
        });
        this.form.billno = response.data?.billno || "";
      } catch (error) {
        console.error(error);
      }
    },
    async fetchShopStateCode() {
      try {
        const response = await axios.get("/v1/company-settings/tax-info");
        this.shopStateCode = response.data?.shop_state_code || null;
      } catch (error) {
        console.error(error);
      }
    },
    async fetchCompanySettings() {
      try {
        this.companySettings = await getCompanySettingsCached();
      } catch (error) {
        console.error(error);
        this.companySettings = {};
      }
    },
    selectCategory(categoryId) {
      this.selectedCategory = categoryId;
      this.fetchItems({ reset: true });
    },
    isInCart(itemid) {
      return this.cart.some((item) => item.itemid === itemid);
    },
    queueProductSearch() {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(() => {
        this.fetchItems({ reset: true });
      }, 250);
    },
    handleProductScroll(event) {
      const target = event.target;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 260) {
        this.fetchItems({ reset: false });
      }
    },
    handleCategoryScroll(event) {
      const target = event.target;
      if (target.scrollLeft + target.clientWidth >= target.scrollWidth - 180) {
        this.fetchCategories({ reset: false });
      }
    },
    handleCategoryWheel(event) {
      const target = event.currentTarget;
      target.scrollLeft += event.deltaY || event.deltaX;
      this.handleCategoryScroll({ target });
    },
    async addProduct(product, quantity = 1) {
      const existing = this.cart.find((item) => item.itemid === product.itemid);
      if (existing) {
        existing.quantity = Number(existing.quantity || 0) + Number(quantity || 1);
        this.calculateCartItem(existing);
        return;
      }

      const cartItem = {
        ...product,
        quantity: Number(quantity || 1),
        rate: this.getSalePrice(product),
        purchaseprice: Number(product.purchaserate || 0),
        discountamt: 0,
        discountvalue: 0,
        taxtype: product.taxtype || "GST",
        taxpercent: Number(product.taxpercent || 0),
        taxablevalue: 0,
        taxamount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        amount: 0,
        istaxinclusive: Boolean(product.istaxinclusive),
        selectedPurchaseItemId: null,
        layerRemainingQty: Number(product.available || 0),
        batchno: "",
        expirydate: null,
      };

      await this.applyFirstPurchaseLayer(cartItem);
      this.calculateCartItem(cartItem);
      this.cart.push(cartItem);
      this.computeTotals();
    },
    async applyFirstPurchaseLayer(item) {
      try {
        const response = await axios.get(`/v1/sales/purchase-layers/${item.itemid}`, {
          params: { locationid: this.getLocationId() || undefined },
        });
        const layer = Array.isArray(response.data) ? response.data[0] : null;
        if (!layer) return;
        item.selectedPurchaseItemId = layer.purchaseitemid || null;
        item.purchaseprice = Number(layer.purchaseprice || item.purchaseprice || 0);
        item.taxpercent = Number(layer.taxpercent ?? item.taxpercent ?? 0);
        item.taxtype = layer.taxtype || item.taxtype || "GST";
        item.istaxinclusive = Boolean(layer.istaxinclusive ?? item.istaxinclusive);
        item.batchno = layer.batchno || "";
        item.expirydate = layer.expirydate || null;
        item.layerRemainingQty = Number(layer.remainingqty || item.available || 0);
      } catch (error) {
        console.error(error);
      }
    },
    calculateCartItem(item) {
      const maxQty = Number(item.layerRemainingQty || item.available || 0);
      let qty = Number(item.quantity || 0);
      if (qty < 1) qty = 1;
      if (maxQty > 0 && qty > maxQty) qty = maxQty;
      item.quantity = qty;

      const rate = Number(item.rate || 0);
      const discountPercent = Number(item.discountamt || 0);
      const taxPercent = Number(item.taxpercent || 0);
      const gross = qty * rate;
      const discountValue = (gross * discountPercent) / 100;
      const discountedGross = Math.max(gross - discountValue, 0);

      item.discountvalue = Number(discountValue.toFixed(2));

      if (item.taxtype === "EXEMPT" || item.taxtype === "NONGST") {
        item.taxablevalue = Number(discountedGross.toFixed(2));
        item.taxamount = 0;
        item.cgst = 0;
        item.sgst = 0;
        item.igst = 0;
        item.amount = Number(discountedGross.toFixed(2));
        this.computeTotals();
        return;
      }

      let taxableValue = 0;
      let taxAmount = 0;
      if (item.istaxinclusive) {
        taxableValue = Number((discountedGross / (1 + taxPercent / 100)).toFixed(2));
        taxAmount = Number((discountedGross - taxableValue).toFixed(2));
      } else {
        taxableValue = Number(discountedGross.toFixed(2));
        taxAmount = Number(((taxableValue * taxPercent) / 100).toFixed(2));
      }

      item.taxablevalue = taxableValue;
      item.taxamount = taxAmount;
      item.amount = item.istaxinclusive ? Number(discountedGross.toFixed(2)) : taxableValue;
      if (this.isInterState) {
        item.cgst = 0;
        item.sgst = 0;
        item.igst = taxAmount;
      } else {
        item.cgst = Number((taxAmount / 2).toFixed(2));
        item.sgst = Number((taxAmount / 2).toFixed(2));
        item.igst = 0;
      }
      this.computeTotals();
    },
    increaseQty(item) {
      item.quantity = Number(item.quantity || 0) + 1;
      this.calculateCartItem(item);
    },
    decreaseQty(item) {
      item.quantity = Math.max(1, Number(item.quantity || 1) - 1);
      this.calculateCartItem(item);
    },
    removeCartItem(index) {
      this.cart.splice(index, 1);
      this.computeTotals();
    },
    clearCart() {
      this.cart = [];
      this.computeTotals();
    },
    computeTotals() {
      let totalAmount = 0;
      let totalTax = 0;
      let taxableValue = 0;
      let cgst = 0;
      let sgst = 0;
      let igst = 0;
      let itemDiscount = 0;

      this.cart.forEach((item) => {
        totalAmount += this.lineTotal(item);
        totalTax += Number(item.taxamount || 0);
        taxableValue += Number(item.taxablevalue || 0);
        cgst += Number(item.cgst || 0);
        sgst += Number(item.sgst || 0);
        igst += Number(item.igst || 0);
        itemDiscount += Number(item.discountvalue || 0);
      });

      const totalDiscount = Number((itemDiscount + this.orderDiscountAmount).toFixed(2));
      const netAmount = Math.max(0, totalAmount - this.orderDiscountAmount + Number(this.form.roundoff || 0));
      this.totals = {
        totalAmount: Number(totalAmount.toFixed(2)),
        taxableValue: Number(taxableValue.toFixed(2)),
        totalDiscount,
        totalTax: Number(totalTax.toFixed(2)),
        cgst: Number(cgst.toFixed(2)),
        sgst: Number(sgst.toFixed(2)),
        igst: Number(igst.toFixed(2)),
        netAmount: Number(netAmount.toFixed(2)),
      };
    },
    addFirstMatchingProduct() {
      const query = this.quickProductSearch.trim().toLowerCase();
      if (!query) return;
      const match = this.products.find((item) =>
        `${item.itemname || ""} ${item.itemcode || ""} ${item.barcode || ""}`.toLowerCase().includes(query)
      );
      if (match) {
        this.addProduct(match);
        this.quickProductSearch = "";
      }
    },
    async handleQuickProductEntry() {
      this.scanText = this.quickProductSearch;
      await this.handleScanSearch();
      if (this.scanText) {
        this.addFirstMatchingProduct();
      }
    },
    async handleScanSearch() {
      const query = this.scanText.trim().toLowerCase();
      if (!query) return;
      let match = this.products.find((item) =>
        [item.barcode, item.itemcode, item.itemname].some((value) => String(value || "").toLowerCase() === query)
      );
      if (!match) {
        this.productSearch = query;
        await this.fetchItems({ reset: true });
        match = this.products.find((item) =>
          [item.barcode, item.itemcode, item.itemname].some((value) => String(value || "").toLowerCase() === query)
        ) || this.products[0];
      }
      if (match) {
        await this.addProduct(match);
        this.scanText = "";
        this.quickProductSearch = "";
      } else {
        Swal.fire("No product found for this scan/search.");
      }
    },
    searchCustomers() {
      clearTimeout(this.customerLookupTimer);
      this.customerLookupTimer = setTimeout(this.fetchCustomerResults, 250);
    },
    async fetchCustomerResults() {
      const search = this.customerSearch.trim();
      if (!search) {
        this.customerSearchResults = [];
        return;
      }
      try {
        const response = await axios.get("/v1/customers", { params: { search, limit: 8 } });
        this.customerSearchResults = response.data || [];
      } catch (error) {
        console.error(error);
      }
    },
    selectCustomer(customer) {
      this.form.customerid = customer.customerid || "";
      this.form.customername = customer.customername || "";
      this.form.mobile = customer.mobile || "";
      this.form.gstno = customer.gstno || "";
      this.customerSearch = customer.customername || customer.mobile || "";
      this.showCustomerResults = false;
      this.cart.forEach((item) => this.calculateCartItem(item));
    },
    clearCustomer() {
      this.form.customerid = "";
      this.form.customername = "";
      this.form.mobile = "";
      this.form.gstno = "";
      this.customerSearch = "";
      this.customerSearchResults = [];
      this.showCustomerResults = false;
    },
    handleOutsideClick(event) {
      if (!this.$el?.querySelector(".pos-lookup")?.contains(event.target)) {
        this.showCustomerResults = false;
      }
    },
    validateSale() {
      if (!this.cart.length) {
        Swal.fire("Please add at least one product.");
        return false;
      }
      const invalid = this.cart.find((item) => !(Number(item.quantity) > 0));
      if (invalid) {
        Swal.fire(`Quantity must be greater than zero for ${invalid.itemname}.`);
        return false;
      }
      return true;
    },
    buildSalePayload() {
      return {
        billno: null,
        billdate: this.form.billdate,
        locationid: this.getLocationId(),
        customerid: this.form.customerid || null,
        customername: this.form.customername || this.customerSearch || "Walk in Customer",
        mobile: this.form.mobile || null,
        isinterstate: this.isInterState,
        customergstin: this.form.gstno || null,
        placeofsupply: this.isInterState ? (this.form.gstno?.substring(0, 2) || null) : this.shopStateCode,
        billtype: this.isInterState ? (this.form.gstno ? "B2B" : "B2C") : "B2C",
        totalamount: this.totals.totalAmount,
        discountamount: this.totals.totalDiscount,
        taxamount: this.totals.totalTax,
        roundoff: Number(this.form.roundoff || 0),
        netamount: this.totals.netAmount,
        paymentmode: this.form.paymentmode,
        is_credit: false,
        paid_amount: this.totals.netAmount,
        remarks: this.form.remarks,
        items: this.cart.map((item) => ({
          itemid: item.itemid,
          locationid: this.getLocationId(),
          purchaseitemid: item.selectedPurchaseItemId || null,
          quantity: Number(item.quantity || 0),
          rate: Number(item.rate || 0),
          taxtype: item.taxtype || "GST",
          taxpercent: Number(item.taxpercent || 0),
          taxablevalue: Number(item.taxablevalue || 0),
          cgst: Number(item.cgst || 0),
          sgst: Number(item.sgst || 0),
          igst: Number(item.igst || 0),
          istaxinclusive: Boolean(item.istaxinclusive),
          discount: Number(item.discountamt || 0),
          taxamount: Number(item.taxamount || 0),
          amount: Number(item.amount || 0),
          batchno: item.batchno || null,
          expirydate: item.expirydate || null,
          freequantity: 0,
        })),
      };
    },
    async submitPosSale() {
      if (!this.validateSale()) return;
      this.saving = true;
      try {
        const response = await axios.post("/v1/sales", this.buildSalePayload());
        const saleid = Array.isArray(response.data) ? response.data[0] : response.data?.saleid;
        let saleDetail = null;
        if (saleid) {
          const detailResponse = await axios.get(`/v1/sales/${saleid}`);
          saleDetail = detailResponse.data;
        }
        Swal.fire({ icon: "success", title: "Sale created", timer: 1200, showConfirmButton: false });
        this.printPosReceipt(saleDetail);
        await this.resetSale({ keepProducts: true });
      } catch (error) {
        console.error(error);
        Swal.fire(error.response?.data?.detail || "An error occurred while saving the POS sale.");
      } finally {
        this.saving = false;
      }
    },
    receiptEscape(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    buildReceiptHtml(saleDetail) {
      const company = this.companySettings || {};
      const items = saleDetail?.items?.length ? saleDetail.items : this.cart;
      const billNo = saleDetail?.billno || this.form.billno || "";
      const billDate = saleDetail?.billdate || this.form.billdate;
      const customerName = saleDetail?.customername || this.form.customername || this.customerSearch || "Walk in Customer";
      const netAmount = Number(saleDetail?.netamount ?? this.totals.netAmount ?? 0);
      const taxAmount = Number(saleDetail?.taxamount ?? this.totals.totalTax ?? 0);
      const discount = Number(saleDetail?.discountamount ?? this.totals.totalDiscount ?? 0);
      const subtotal = items.reduce((sum, item) => sum + this.lineTotal(item), 0);
      const taxBreakdown = this.receiptTaxBreakdown(items);
      const taxRows = [
        taxBreakdown.cgst ? `<div class="line tax-line"><strong>CGST :</strong><span>${this.formatCurrency(taxBreakdown.cgst)}</span></div>` : "",
        taxBreakdown.sgst ? `<div class="line tax-line"><strong>SGST :</strong><span>${this.formatCurrency(taxBreakdown.sgst)}</span></div>` : "",
        taxBreakdown.igst ? `<div class="line tax-line"><strong>IGST :</strong><span>${this.formatCurrency(taxBreakdown.igst)}</span></div>` : "",
      ].join("");

      const rows = items.map((item, index) => `
        <tr>
          <td>${index + 1}. ${this.receiptEscape(item.itemname || item.itemid)}</td>
          <td>${this.formatCurrency(item.rate)}</td>
          <td>${this.formatQty(item.quantity)}</td>
          <td>${this.formatCurrency(this.lineTotal(item))}</td>
        </tr>
      `).join("");

      return `<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>POS Receipt ${this.receiptEscape(billNo)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 18px; background: #f1f5f9; font-family: Arial, sans-serif; color: #1f2937; }
    .receipt { width: 330px; margin: 0 auto; background: #fff; padding: 24px 24px 18px; box-shadow: 0 16px 40px rgba(15,23,42,.12); }
    .brand { text-align: center; color: #12345a; font-size: 22px; font-weight: 800; margin-bottom: 8px; }
    .co { text-align: center; font-size: 12px; line-height: 1.45; color: #6b7280; }
    .co strong { display: block; color: #1f2937; font-size: 13px; margin-bottom: 4px; }
    .title { display: flex; align-items: center; gap: 10px; margin: 12px 0; color: #1f2937; font-weight: 800; justify-content: center; }
    .title:before, .title:after { content: ""; height: 1px; flex: 1; border-top: 1px dashed #d1d5db; }
    .info { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; font-size: 11px; color: #4b5563; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { text-align: left; padding: 8px 4px; border-top: 1px dashed #d1d5db; border-bottom: 1px dashed #d1d5db; color: #374151; }
    td { padding: 7px 4px; color: #4b5563; vertical-align: top; }
    th:nth-child(n+2), td:nth-child(n+2) { text-align: right; }
    .totals { border-top: 1px dashed #d1d5db; margin-top: 8px; padding-top: 8px; display: grid; gap: 10px; font-size: 12px; color: #6b7280; }
    .line { display: flex; justify-content: space-between; gap: 12px; }
    .tax-line { color: #4b5563; }
    .payable { margin-top: 14px; padding-top: 14px; border-top: 1px dashed #d1d5db; color: #0f2f5f; font-weight: 800; font-size: 15px; }
    .note { margin-top: 18px; text-align: center; font-size: 11px; color: #6b7280; line-height: 1.45; }
    .barcode { margin: 18px auto 8px; width: 140px; height: 46px; background: repeating-linear-gradient(90deg,#0f2f5f 0 2px,#fff 2px 4px,#0f2f5f 4px 5px,#fff 5px 8px); }
    .sale-no { text-align:center; font-size: 11px; font-weight: 800; color: #0f2f5f; }
    .print { display:block; margin: 18px auto 0; border: 0; background:#ff9f43; color:#fff; height:34px; padding:0 16px; border-radius:5px; font-weight:700; cursor:pointer; }
    @media print {
      @page { size: 80mm auto; margin: 0; }
      body { padding: 0; background:#fff; }
      .receipt { width: 80mm; box-shadow: none; }
      .print { display:none; }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="brand">${this.receiptEscape(company.DisplayName || company.CompanyName || "POS")}</div>
    <div class="co">
      <strong>${this.receiptEscape(company.CompanyName || "Dreamguys Technologies Pvt Ltd.")}</strong>
      ${company.Phone1 ? `Phone Number: ${this.receiptEscape(company.Phone1)}<br/>` : ""}
      ${company.Email ? `Email: ${this.receiptEscape(company.Email)}` : ""}
    </div>
    <div class="title">Tax Invoice</div>
    <div class="info">
      <span>Name: ${this.receiptEscape(customerName)}</span>
      <span>Customer Id: ${this.receiptEscape(saleDetail?.customerid || this.form.customerid || "-")}</span>
      <span>Invoice No: ${this.receiptEscape(billNo)}</span>
      <span>Date: ${this.receiptEscape(billDate || "-")}</span>
    </div>
    <table>
      <thead><tr><th># Item</th><th>Price</th><th>Qty</th><th>Total</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="totals">
      <div class="line"><strong>Sub Total :</strong><span>${this.formatCurrency(subtotal)}</span></div>
      <div class="line"><strong>Discount :</strong><span>-${this.formatCurrency(discount)}</span></div>
      <div class="line"><strong>Tax :</strong><span>${this.formatCurrency(taxAmount)}</span></div>
      ${taxRows}
      <div class="line"><strong>Due :</strong><span>${this.formatCurrency(0)}</span></div>
      <div class="line payable"><span>Total Payable :</span><span>${this.formatCurrency(netAmount)}</span></div>
    </div>
    <div class="note">**VAT/GST against this challan is payable through central registration. Thank you for your business!</div>
    <div class="barcode"></div>
    <div class="sale-no">Sale ${this.receiptEscape(billNo || "")}</div>
    <div class="note">Thank You For Shopping With Us. Please Come Again</div>
    <button class="print" onclick="window.print()">Print Receipt</button>
  </div>
</body>
</html>`;
    },
    receiptTaxBreakdown(items) {
      return items.reduce(
        (acc, item) => {
          acc.cgst += Number(item.cgst || 0);
          acc.sgst += Number(item.sgst || 0);
          acc.igst += Number(item.igst || 0);
          return acc;
        },
        { cgst: 0, sgst: 0, igst: 0 }
      );
    },
    printPosReceipt(saleDetail) {
      printBillHtml(this.buildReceiptHtml(saleDetail), (text) => {
        Swal.fire({ icon: "error", title: "Print Error", text });
      });
    },
    async resetSale(options = {}) {
      this.form = this.getInitialForm();
      this.cart = [];
      this.customerSearch = "";
      this.customerSearchResults = [];
      this.productSearch = "";
      this.quickProductSearch = "";
      this.scanText = "";
      this.orderDiscountPercent = 0;
      this.totals = this.getInitialTotals();
      await this.fetchNextBillNo();
      if (!options.keepProducts) await this.fetchItems();
    },
  },
};
</script>

<style scoped>
.pos-main-wrapper,
.pos-page-wrapper {
  background: #f8f9fb;
  color: #1f2937;
}

.pos-main-wrapper {
  min-height: 100vh;
  padding-top: 78px;
}

.pos-page-wrapper {
  margin: 0 !important;
  min-height: calc(100vh - 78px);
  padding: 0 !important;
  width: 100%;
}

.dream-pos-header {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #edf0f3;
  display: flex;
  height: 78px;
  justify-content: space-between;
  left: 0;
  padding: 0 30px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1001;
}

.dream-pos-left,
.dream-pos-right {
  align-items: center;
  display: flex;
  gap: 16px;
}

.dream-logo img {
  display: block;
  height: 49px;
  object-fit: contain;
  width: 158px;
}

.dream-pos-label {
  color: #ff9f43;
  font-size: 15px;
  font-weight: 800;
  margin-left: 4px;
}

.dream-clock {
  align-items: center;
  background: #259d8f;
  border-radius: 5px;
  color: #fff;
  display: inline-flex;
  font-size: 17px;
  font-weight: 700;
  gap: 10px;
  height: 38px;
  padding: 0 17px;
}

.dream-dashboard-btn,
.dream-store-btn,
.dream-icon-btn {
  align-items: center;
  border: 0;
  display: inline-flex;
  justify-content: center;
}

.dream-dashboard-btn {
  background: #6d31e8;
  border-radius: 6px;
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  gap: 8px;
  height: 39px;
  padding: 0 19px;
  text-decoration: none;
}

.dream-store-btn {
  background: #fff;
  border: 1px solid #e5e9ef;
  border-radius: 8px;
  color: #3f4652;
  font-size: 16px;
  gap: 10px;
  height: 40px;
  padding: 0 14px;
  text-decoration: none;
}

.dream-store-btn .fa-store {
  background: #0e5c25;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  padding: 5px;
}

.dream-divider {
  background: #e5e9ef;
  height: 32px;
  width: 1px;
}

.dream-icon-btn {
  background: #f7f8fa;
  border-radius: 8px;
  color: #273b56;
  height: 39px;
  text-decoration: none;
  width: 39px;
}

.dream-icon-btn.active {
  background: #e95b1b;
  color: #fff;
}

.dream-avatar {
  border-radius: 8px;
  height: 39px;
  object-fit: cover;
  width: 39px;
}

.pos-content {
  padding: 24px 30px 28px;
}

.pos-actions-bar {
  align-items: center;
  background: #fff;
  border: 1px solid #e7eaee;
  border-radius: 8px;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 29px;
  min-height: 71px;
  padding: 14px;
}

.pos-action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pos-top-btn {
  align-items: center;
  border: 0;
  border-radius: 6px;
  color: #fff;
  display: inline-flex;
  font-weight: 700;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  text-decoration: none;
}

.pos-top-btn-green { background: #249d8d; }
.pos-top-btn-violet { background: #4834d4; }
.pos-top-btn-blue { background: #3f5efb; }

.pos-action-spacer {
  display: block;
  flex: 1;
}

.pos-search,
.pos-product-select,
.pos-lookup {
  align-items: center;
  background: #fff;
  border: 1px solid #e1e6ed;
  border-radius: 7px;
  display: flex;
  min-height: 42px;
  color: #8a94a3;
}

.pos-product-select > i,
.pos-search > i {
  padding-left: 14px;
}

.pos-search input,
.pos-product-select input,
.pos-lookup input {
  border: 0;
  flex: 1;
  height: 40px;
  min-width: 0;
  outline: 0;
  padding: 0 10px;
}

.pos-product-select button,
.pos-lookup > button {
  background: transparent;
  border: 0;
  color: #253858;
  height: 40px;
  padding: 0 12px;
}

.pos-grid {
  display: grid;
  gap: 30px;
  grid-template-columns: minmax(0, 1fr) 530px;
}

.pos-section {
  margin-bottom: 30px;
}

.pos-section-title-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.pos-section-title-row h4,
.pos-order-head h3,
.pos-customer-section h4,
.pos-cart-title h4,
.pos-payment-section h4 {
  color: #202b3c;
  font-size: 22px;
  font-weight: 800;
  margin: 0;
}

.pos-section-title-row span {
  color: #7b8492;
  font-weight: 600;
}

.pos-category-scroller {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 0 12px;
  overscroll-behavior-inline: contain;
  scroll-snap-type: x mandatory;
  scrollbar-width: thin;
}

.pos-category-card {
  align-items: center;
  background: #fff;
  border: 1px solid #e4e8ee;
  border-radius: 7px;
  color: #1f3354;
  display: flex;
  flex-direction: column;
  flex: 0 0 166px;
  gap: 8px;
  min-height: 154px;
  padding: 24px 10px 20px;
  scroll-snap-align: start;
}

.pos-category-card.active {
  border-color: #ff9f43;
  color: #ff9f43;
}

.pos-category-card img {
  height: 44px;
  object-fit: contain;
  width: 48px;
}

.pos-category-card small {
  color: #9aa3af;
  font-size: 13px;
  font-weight: 700;
}

.pos-products-head {
  gap: 16px;
}

.pos-product-count-text {
  color: #7b8492;
  display: block;
  font-size: 13px;
  font-weight: 700;
  margin-top: 4px;
}

.pos-search {
  max-width: 280px;
  padding-left: 12px;
  width: 40%;
}

.pos-product-grid {
  display: grid;
  gap: 30px;
  grid-template-columns: repeat(4, minmax(170px, 1fr));
}

.pos-product-scroll {
  max-height: calc(100vh - 172px);
  overflow-y: auto;
  padding-right: 4px;
  scroll-behavior: smooth;
}

.pos-product-card {
  background: #fff;
  border: 1px solid #e1e6ed;
  border-radius: 8px;
  color: #1f2937;
  min-height: 344px;
  padding: 20px;
  position: relative;
  text-align: left;
}

.pos-product-card.selected {
  border-color: #45c081;
}

.pos-check {
  align-items: center;
  background: #45c081;
  border-radius: 50%;
  color: #fff;
  display: flex;
  height: 20px;
  justify-content: center;
  position: absolute;
  right: 14px;
  top: 14px;
  width: 20px;
}

.pos-product-image {
  align-items: center;
  background: #fafafa;
  border-radius: 7px;
  display: flex;
  height: 198px;
  justify-content: center;
  margin-bottom: 14px;
}

.pos-product-image img {
  max-height: 174px;
  max-width: 90%;
  object-fit: contain;
}

.pos-product-meta {
  color: #7c8695;
  display: block;
  margin-bottom: 3px;
}

.pos-product-card strong {
  display: block;
  font-size: 16px;
  min-height: 40px;
}

.pos-product-footer {
  align-items: center;
  border-top: 1px dashed #e1e6ed;
  color: #ed4aa4;
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
}

.pos-product-footer strong {
  color: #239c8c;
  min-height: 0;
}

.pos-order-card {
  background: #fff;
  border-radius: 0;
  margin: -24px -30px -28px 0;
  min-height: 100vh;
  padding: 24px 30px 28px;
}

.pos-order-head {
  align-items: center;
  background: #f7f8fa;
  border-radius: 7px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 26px;
  padding: 16px 14px;
}

.pos-order-head p {
  color: #7b8492;
  font-size: 17px;
  margin: 3px 0 0;
}

.pos-order-head button {
  background: transparent;
  border: 0;
  color: #ff3b30;
}

.pos-customer-section,
.pos-cart-section,
.pos-payment-section {
  border-bottom: 1px solid #e7eaee;
  margin-bottom: 22px;
  padding-bottom: 22px;
}

.pos-selected-product {
  align-items: center;
  border: 1px solid #e7eaee;
  border-radius: 8px;
  display: grid;
  gap: 12px;
  grid-template-columns: 70px minmax(0, 1fr) 112px 62px;
  margin-top: 12px;
  padding: 10px;
}

.pos-selected-product img {
  background: #fafafa;
  border-radius: 7px;
  height: 64px;
  object-fit: contain;
  width: 64px;
}

.pos-selected-info {
  min-width: 0;
}

.pos-selected-info span,
.pos-selected-info small {
  color: #7b8492;
  display: block;
  font-size: 12px;
}

.pos-selected-info strong {
  color: #202b3c;
  display: block;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pos-selected-qty {
  align-items: center;
  border: 1px solid #e1e6ed;
  border-radius: 7px;
  display: flex;
  height: 40px;
}

.pos-selected-qty button {
  background: #fff;
  border: 0;
  color: #294466;
  height: 38px;
  width: 32px;
}

.pos-selected-qty input {
  border: 0;
  font-weight: 800;
  min-width: 0;
  text-align: center;
  width: 44px;
}

.pos-add-selected {
  background: #ff9f43;
  border: 0;
  border-radius: 7px;
  color: #fff;
  font-weight: 800;
  height: 40px;
}

.pos-customer-row {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) 48px;
  margin: 16px 0 12px;
}

.pos-lookup {
  position: relative;
}

.pos-lookup-results {
  background: #fff;
  border: 1px solid #e1e6ed;
  border-radius: 7px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
  left: 0;
  max-height: 230px;
  overflow: auto;
  position: absolute;
  right: 0;
  top: 45px;
  z-index: 5;
}

.pos-lookup-results button {
  background: #fff;
  border: 0;
  border-bottom: 1px solid #f0f2f5;
  display: block;
  padding: 10px 12px;
  text-align: left;
  width: 100%;
}

.pos-lookup-results span {
  color: #7b8492;
  display: block;
  font-size: 12px;
}

.pos-add-customer {
  background: #ff9f43;
  border: 0;
  border-radius: 8px;
  color: #fff;
  font-size: 18px;
}

.pos-cart-title {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.pos-cart-title span {
  background: #ff9f43;
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
  padding: 2px 7px;
}

.pos-cart-title button {
  background: transparent;
  border: 0;
  color: #ff3b30;
}

.pos-cart-list {
  display: grid;
  gap: 10px;
  max-height: 353px;
  overflow: auto;
  padding-right: 4px;
}

.pos-cart-item {
  align-items: center;
  border: 1px solid #e7eaee;
  border-radius: 8px;
  display: grid;
  gap: 10px;
  grid-template-columns: 96px minmax(0, 1fr) 94px 36px;
  min-height: 126px;
  padding: 12px;
}

.pos-cart-item img {
  background: #fafafa;
  border-radius: 7px;
  height: 92px;
  object-fit: contain;
  width: 92px;
}

.pos-cart-info strong {
  color: #1f2937;
  display: block;
  margin: 4px 0;
}

.pos-code {
  background: #ffae57;
  border-radius: 4px;
  color: #fff;
  display: inline-block;
  font-size: 12px;
  font-weight: 800;
  padding: 3px 7px;
}

.pos-cart-info > span:last-child {
  color: #239c8c;
  font-weight: 800;
}

.pos-cart-qty {
  align-items: center;
  display: flex;
  gap: 4px;
}

.pos-cart-qty button,
.pos-cart-remove {
  background: transparent;
  border: 0;
  color: #294466;
}

.pos-cart-qty input {
  border: 0;
  font-weight: 800;
  text-align: center;
  width: 36px;
}

.pos-cart-remove {
  border: 1px solid #e1e6ed;
  border-radius: 6px;
  height: 36px;
}

.pos-adjustments {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 24px;
}

.pos-adjustments span {
  color: #374151;
  display: block;
  font-weight: 700;
  margin-bottom: 8px;
}

.pos-adjustments input,
.pos-adjustments select {
  border: 1px solid #e1e6ed;
  border-radius: 7px;
  height: 42px;
  padding: 0 12px;
  width: 100%;
}

.pos-total-box {
  background: #f7f8fa;
  border-radius: 8px;
  display: grid;
  gap: 17px;
  margin-bottom: 26px;
  padding: 26px 30px;
}

.pos-total-box div {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.pos-total-box span {
  color: #7b8492;
}

.pos-total-box strong {
  color: #6b7280;
  font-size: 17px;
}

.pos-total-box .danger {
  color: #ff3b30;
}

.pos-total-line {
  border-top: 1px solid #e2e6ed;
  margin-top: 6px;
  padding-top: 16px;
}

.pos-payment-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, 1fr);
}

.pos-payment-grid button {
  align-items: center;
  background: #fff;
  border: 1px solid #e1e6ed;
  border-radius: 8px;
  color: #27324f;
  display: flex;
  flex-direction: column;
  font-weight: 700;
  gap: 8px;
  min-height: 82px;
  justify-content: center;
}

.pos-payment-grid button.active {
  border-color: #ff9f43;
  color: #e76f1c;
}

.pos-submit-btn {
  background: #ff9f43;
  border: 0;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  height: 48px;
  margin-top: 16px;
  width: 100%;
}

.pos-submit-btn:disabled {
  cursor: not-allowed;
  opacity: .65;
}

.pos-empty-panel,
.pos-empty-cart,
.pos-load-more {
  align-items: center;
  background: #fff;
  border: 1px dashed #d7dde6;
  border-radius: 8px;
  color: #7b8492;
  display: flex;
  justify-content: center;
  min-height: 140px;
  text-align: center;
}

.pos-load-more {
  margin-top: 14px;
  min-height: 54px;
}

@media (max-width: 1400px) {
  .pos-grid {
    grid-template-columns: minmax(0, 1fr) 430px;
  }

  .pos-category-grid {
    grid-template-columns: repeat(4, minmax(120px, 1fr));
  }

  .pos-product-grid {
    grid-template-columns: repeat(3, minmax(165px, 1fr));
  }
}

@media (max-width: 991px) {
  .dream-pos-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
    height: auto;
    padding: 14px 16px;
  }

  .dream-pos-left,
  .dream-pos-right {
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
  }

  .dream-pos-right {
    justify-content: flex-start;
  }

  .dream-logo img {
    height: 38px;
    width: 122px;
  }

  .pos-main-wrapper {
    padding-top: 126px;
  }

  .pos-page-wrapper {
    min-height: calc(100vh - 126px);
  }

  .pos-actions-bar,
  .pos-grid {
    display: block;
  }

  .pos-order-card {
    margin: 0;
    min-height: 0;
    padding: 24px;
  }
}

@media (max-width: 767px) {
  .dream-dashboard-btn,
  .dream-store-btn {
    font-size: 13px;
    height: 36px;
    padding: 0 12px;
  }

  .dream-clock {
    font-size: 13px;
    height: 34px;
    padding: 0 12px;
  }

  .dream-icon-btn,
  .dream-avatar {
    height: 36px;
    width: 36px;
  }

  .pos-main-wrapper {
    padding-top: 146px;
  }

  .pos-page-wrapper {
    min-height: calc(100vh - 146px);
  }

  .pos-content {
    padding: 16px;
  }

  .pos-product-grid,
  .pos-adjustments,
  .pos-payment-grid {
    grid-template-columns: 1fr;
  }

  .pos-category-card {
    flex-basis: 150px;
  }

  .pos-selected-product {
    grid-template-columns: 56px minmax(0, 1fr);
  }

  .pos-selected-qty,
  .pos-add-selected {
    grid-column: 2;
  }

  .pos-cart-item {
    grid-template-columns: 62px minmax(0, 1fr);
  }

  .pos-cart-qty,
  .pos-cart-remove {
    grid-column: 2;
  }
}
</style>
